import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import useMessage from '../lib/useMessage';
import InlineMessage from '../components/InlineMessage';
import Overlay from '../components/Overlay';

// Relay heartbeat freshness window. Printer counts as "Online" if last_seen_at
// falls inside this window. 5 min — tight enough to catch a real outage within
// a shift, loose enough to survive normal network blips. One constant, easy to
// tune once a real relay agent is live and we see real-world flap behavior.
const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

const MODEL_OPTIONS = ['ZD421', 'ZD621', 'ZQ610', 'ZQ620'];

function fmtAgo(ms) {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

// Status is computed client-side from last_seen_at — printer_registry has no
// stored "status" column. Three buckets: never connected (null), online
// (inside threshold), offline (stale heartbeat).
function getPrinterStatus(p) {
  if (!p.last_seen_at) {
    return { key: 'never', label: 'Never connected', sub: 'registered, awaiting first heartbeat' };
  }
  const diffMs = Date.now() - new Date(p.last_seen_at).getTime();
  if (diffMs < ONLINE_THRESHOLD_MS) {
    return { key: 'online', label: 'Online', sub: `seen ${fmtAgo(diffMs)}` };
  }
  return { key: 'offline', label: 'Offline', sub: `seen ${fmtAgo(diffMs)} — check relay agent` };
}

const STATUS_COLOR = {
  online: { dot: '#1D9E75', text: 'var(--green-dk)' },
  offline: { dot: '#9A9A9A', text: 'var(--gray-lt)' },
  never: { dot: '#D4D4D4', text: '#9A9A9A' },
};

// Placeholder ZPL for the "Send test label" action. Full label-template
// generation is a separate, not-yet-built session (Zebra ZPL label sprint) —
// this exists only to exercise the real print_jobs → relay → status loop
// end-to-end. Swap the payload builder when that session ships; nothing about
// the job/status plumbing below needs to change.
function buildTestZpl(printer) {
  const ts = new Date().toLocaleString();
  return `^XA\n^CF0,30\n^FO20,20^FDSTEDE TEST PRINT^FS\n^CF0,22\n^FO20,60^FD${printer.name}^FS\n^FO20,95^FD${ts}^FS\n^XZ`;
}

const TEST_JOB_DISPLAY = {
  pending: { text: 'Job queued — waiting on relay agent', color: '#96650E' },
  sent: { text: 'Sent to printer — waiting for confirmation', color: '#1D5FA8' },
  done: { text: '✓ Test label printed — confirmed by printer', color: '#166C50' },
  error: { text: null, color: '#C62828' }, // text filled from error_message
  canceled: { text: 'Canceled', color: '#9A9A9A' },
};

export function PrinterRegistryPage() {
  const { user } = useAuth();
  const [printers, setPrinters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testJobs, setTestJobs] = useState({}); // printerId -> { status, error_message }
  const testChannelsRef = useRef({}); // printerId -> realtime channel, cleaned up on unmount

  const pageMsg = useMessage();
  const panelMsg = useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    // Session 51 fix — same branches!branch_id disambiguation as
    // printLabel.js's fetchActivePrinters. Without it this silently
    // returns an empty printer list instead of an error — exactly the
    // kind of silent failure the COUNT(*)-verification habit exists to
    // catch, just on a SELECT instead of a DELETE this time.
    const [{ data: printerData }, { data: branchData }] = await Promise.all([
      supabase.from('printer_registry').select('*, branches!branch_id(name)').order('name'),
      supabase.from('branches').select('*').order('name'),
    ]);
    setPrinters(printerData || []);
    setBranches(branchData || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Live status updates. Requires printer_registry to be added to the
  // supabase_realtime publication (not automatic — see Session 48 handoff).
  // If that hasn't been run yet, this subscribes harmlessly and the page
  // simply falls back to whatever `load()` last fetched.
  useEffect(() => {
    const channel = supabase
      .channel('printer_registry_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'printer_registry' }, () => {
        load();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [load]);

  // Clean up any open test-job subscriptions on unmount.
  useEffect(() => {
    return () => {
      Object.values(testChannelsRef.current).forEach(ch => supabase.removeChannel(ch));
    };
  }, []);

  function openAdd() {
    setEditingPrinter(null);
    setForm({
      name: '',
      branch_id: '',
      ip_address: '',
      port: 9100,
      model: 'ZD421',
      is_active: true,
    });
    panelMsg.clearMessage();
    setPanelOpen(true);
  }

  function openEdit(p) {
    setEditingPrinter(p);
    setForm({
      name: p.name || '',
      branch_id: p.branch_id || '',
      ip_address: p.ip_address || '',
      port: p.port ?? 9100,
      model: p.model || 'ZD421',
      is_active: p.is_active,
    });
    panelMsg.clearMessage();
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditingPrinter(null);
  }

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  async function savePrinter() {
    if (!form.name?.trim()) { panelMsg.showError('Printer name is required.'); return; }
    if (!form.branch_id) { panelMsg.showError('Branch is required.'); return; }
    if (!form.ip_address?.trim()) { panelMsg.showError('IP address is required.'); return; }
    const portNum = parseInt(form.port, 10);
    if (!portNum || portNum <= 0) { panelMsg.showError('Port must be a valid number.'); return; }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      branch_id: form.branch_id,
      ip_address: form.ip_address.trim(),
      port: portNum,
      model: form.model,
      is_active: form.is_active,
    };

    const { error } = editingPrinter
      ? await supabase.from('printer_registry').update(payload).eq('id', editingPrinter.id)
      : await supabase.from('printer_registry').insert(payload);

    if (error) {
      panelMsg.showError('Error saving printer: ' + error.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    closePanel();
    load();
  }

  async function toggleActive(p) {
    await supabase.from('printer_registry').update({ is_active: !p.is_active }).eq('id', p.id);
    load();
  }

  // Phase 4 gate, screen 5 / callout L — Session 77. The control that makes
  // branches.receive_default_printer_id settable at all.
  //
  // #340 found this column read in exactly one place (printLabel.js's
  // fetchBranchPrinterOptions) and written in NONE. The Ride-Along Queue said it
  // was "settable from PrinterRegistryPage"; it never was. So the receive flow's
  // whole Zebra architecture — resolveAutoPrintOption preferring this column,
  // both receive paths branching to dispatchZebraPrint — sat behind a value no
  // screen could produce. This is the missing half.
  //
  // An IMMEDIATE single-table write rather than a field on the form, and that is
  // a deliberate call against the mockup's implied grouping. Saving it with the
  // printer would mean writing printer_registry and branches in one action,
  // which locked rule 7 sends to a Postgres function. There is no transaction to
  // protect here — it is one column on one row — so the honest shape is its own
  // action, matching "Send test label" and the Active checkbox, both of which
  // already act immediately in this same panel.
  //
  // One printer per branch by construction: the column is a single FK, so
  // setting B where A was default replaces it in the same UPDATE. No sweep, no
  // race between two rows claiming it.
  async function setReceiveDefault(printer, makeDefault) {
    const { error } = await supabase
      .from('branches')
      .update({ receive_default_printer_id: makeDefault ? printer.id : null })
      .eq('id', printer.branch_id);

    if (error) {
      panelMsg.showError('Could not change the receive default: ' + error.message);
      return;
    }
    load();
  }

  async function sendTestLabel(printer) {
    setTestJobs(prev => ({ ...prev, [printer.id]: { status: 'pending' } }));

    const { data, error } = await supabase
      .from('print_jobs')
      .insert({
        branch_id: printer.branch_id,
        printer_id: printer.id,
        requested_by: user.id,
        item_type: 'test',
        copies: 1,
        zpl_payload: buildTestZpl(printer),
        status: 'pending',
      })
      .select()
      .single();

    if (error) {
      setTestJobs(prev => ({ ...prev, [printer.id]: { status: 'error', error_message: error.message } }));
      return;
    }

    // Clean up any prior subscription for this printer before opening a new one
    if (testChannelsRef.current[printer.id]) {
      supabase.removeChannel(testChannelsRef.current[printer.id]);
    }

    const channel = supabase
      .channel(`print_job_${data.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'print_jobs', filter: `id=eq.${data.id}` },
        (payload) => {
          setTestJobs(prev => ({
            ...prev,
            [printer.id]: { status: payload.new.status, error_message: payload.new.error_message },
          }));
          if (['done', 'error', 'canceled'].includes(payload.new.status)) {
            supabase.removeChannel(channel);
            delete testChannelsRef.current[printer.id];
          }
        }
      )
      .subscribe();

    testChannelsRef.current[printer.id] = channel;
  }

  const onlineCount = printers.filter(p => getPrinterStatus(p).key === 'online').length;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">Label printers</div>
          <div className="page-sub">{printers.length} registered · {onlineCount} online</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add printer</button>
      </div>

      <InlineMessage type={pageMsg.type} message={pageMsg.message} onDismiss={pageMsg.clearMessage} />

      {loading ? (
        <div className="loading"><div className="spinner" />Loading...</div>
      ) : printers.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🖨️</div>
            <div className="empty-state-title">No printers registered yet</div>
          </div>
        </div>
      ) : isMobile ? (
        <div className="flex" style={{ flexDirection: 'column', gap: '10px' }}>
          {printers.map(p => {
            const status = getPrinterStatus(p);
            const colors = STATUS_COLOR[status.key];
            return (
              <div
                key={p.id}
                className="card"
                style={{ padding: '12px 14px', cursor: 'pointer' }}
                onClick={() => openEdit(p)}
              >
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{p.name}</div>
                    <div className="mono text-muted" style={{ fontSize: '12px' }}>{p.ip_address}:{p.port}</div>
                  </div>
                  <span className="badge badge-spare">{p.branches?.name || '—'}</span>
                </div>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <div className="flex gap-1" style={{ alignItems: 'center' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.dot, display: 'inline-block' }} />
                    <span style={{ fontSize: '12px', color: colors.text }}>{status.label} · {status.sub.replace('seen ', '').replace(' — check relay agent', '')}</span>
                  </div>
                  <label onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <input type="checkbox" checked={p.is_active} onChange={() => toggleActive(p)} />
                    Active
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Branch</th>
                  <th>IP : Port</th>
                  <th>Model</th>
                  <th>Status</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {printers.map(p => {
                  const status = getPrinterStatus(p);
                  const colors = STATUS_COLOR[status.key];
                  return (
                    <tr key={p.id} onClick={() => openEdit(p)} style={{ cursor: 'pointer' }}>
                      <td><strong>{p.name}</strong></td>
                      <td>{p.branches?.name || <span className="text-muted">—</span>}</td>
                      <td className="mono">{p.ip_address}:{p.port}</td>
                      <td>{p.model}</td>
                      <td>
                        <div className="flex gap-1" style={{ alignItems: 'center' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.dot, display: 'inline-block' }} />
                          <div>
                            <div style={{ fontSize: '13px', color: colors.text, fontWeight: 600 }}>{status.label}</div>
                            <div style={{ fontSize: '11px', color: '#9A9A9A' }}>{status.sub}</div>
                          </div>
                        </div>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={p.is_active} onChange={() => toggleActive(p)} />
                      </td>
                      <td>
                        <span style={{ opacity: 0.5, fontSize: '16px', padding: '2px 6px' }}>⋯</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {panelOpen && (
        <PrinterDetailPanel
          isMobile={isMobile}
          printer={editingPrinter}
          branches={branches}
          form={form}
          setField={f}
          onClose={closePanel}
          onSave={savePrinter}
          saving={saving}
          msg={panelMsg}
          testJob={editingPrinter ? testJobs[editingPrinter.id] : null}
          onSendTest={editingPrinter ? () => sendTestLabel(editingPrinter) : null}
          isReceiveDefault={
            !!editingPrinter &&
            branches.find(b => b.id === editingPrinter.branch_id)?.receive_default_printer_id === editingPrinter.id
          }
          onSetReceiveDefault={editingPrinter ? (v) => setReceiveDefault(editingPrinter, v) : null}
        />
      )}
    </div>
  );
}

// Shared detail surface for both the desktop slideover and mobile bottom
// sheet — one data/behavior layer, two shells, matching the CanonM5Panel
// precedent from Session 47. Portal-rendered per the architecture rule
// against ad hoc `position: fixed` in non-portal components.
function PrinterDetailPanel({
  isMobile, printer, branches, form, setField, onClose, onSave, saving, msg, testJob, onSendTest,
  isReceiveDefault, onSetReceiveDefault,
}) {
  const isNew = !printer;
  const status = printer ? getPrinterStatus(printer) : null;
  const statusColors = status ? STATUS_COLOR[status.key] : null;
  const branchName = printer ? (printer.branches?.name || branches.find(b => b.id === printer.branch_id)?.name) : null;
  // Callout L: a receive-default checkbox on a printer with no relay agent looks
  // like a working feature. It is not - nothing collects the queued job. The
  // panel says so plainly rather than letting the checkbox imply readiness.
  const relayMissing = !!printer && status?.key !== 'online';

  const testDisplay = testJob
    ? (testJob.status === 'error'
        ? { text: `✗ ${testJob.error_message || 'Print failed'}`, color: TEST_JOB_DISPLAY.error.color }
        : TEST_JOB_DISPLAY[testJob.status] || null)
    : null;

  const body = (
    <>
      <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

      <div className="form-group">
        <label className="form-label">Printer name *</label>
        <input className="form-input" value={form.name || ''} onChange={e => setField('name', e.target.value)} placeholder="e.g. Front Counter ZD421" />
      </div>

      <div className="form-group">
        <label className="form-label">Branch *</label>
        <select className="form-select" value={form.branch_id || ''} onChange={e => setField('branch_id', e.target.value)}>
          <option value="">Select branch</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">IP address *</label>
        <input className="form-input mono" value={form.ip_address || ''} onChange={e => setField('ip_address', e.target.value)} placeholder="10.0.1.42" />
        <div style={{ fontSize: '11px', color: '#9A9A9A', marginTop: '4px' }}>LAN IP of the printer — must be reachable from the relay agent's machine.</div>
      </div>

      <div className="form-group">
        <label className="form-label">Port *</label>
        <input className="form-input mono" value={form.port ?? ''} onChange={e => setField('port', e.target.value)} placeholder="9100" />
        <div style={{ fontSize: '11px', color: '#9A9A9A', marginTop: '4px' }}>Default raw ZPL passthrough port.</div>
      </div>

      <div className="form-group">
        <label className="form-label">Model</label>
        <select className="form-select" value={form.model || 'ZD421'} onChange={e => setField('model', e.target.value)}>
          {MODEL_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
          <input type="checkbox" checked={!!form.is_active} onChange={e => setField('is_active', e.target.checked)} />
          Active
        </label>
      </div>

      {!isNew && (
        <>
          {/* .form-group carries no bottom margin of its own — the other groups
              are spaced by their inputs, and this one has none, so it needs the
              gap explicitly or it collides with the relay section below. */}
          <div className="form-group" style={{ borderTop: '1px solid #F0F0F0', paddingTop: '14px', marginBottom: '18px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={!!isReceiveDefault}
                onChange={e => onSetReceiveDefault?.(e.target.checked)}
                style={{ marginTop: '2px', flexShrink: 0 }}
              />
              <span>
                Set as receive default
                <span style={{ display: 'block', fontSize: '11px', color: '#9A9A9A', marginTop: '2px' }}>
                  {isReceiveDefault
                    ? `Labels print here automatically when stock is received at ${branchName || 'this branch'}`
                    : `Labels do not print here automatically when stock is received at ${branchName || 'this branch'}`}
                </span>
              </span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Relay agent status</label>
            <div className="flex gap-1" style={{ alignItems: 'center' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors.dot, display: 'inline-block' }} />
              <span style={{ fontSize: '12px', color: statusColors.text }}>
                {status.key === 'online' ? `Heartbeat received ${status.sub.replace('seen ', '')}` : status.sub}
              </span>
            </div>
            {relayMissing && (
              <div style={{ fontSize: '11px', color: '#96650E', background: '#FFF8E8', border: '1px solid #F2D69A', borderRadius: '8px', padding: '8px 10px', marginTop: '8px' }}>
                No relay agent is running at this branch. Jobs sent here will queue and wait.
              </div>
            )}
          </div>

          <div className="form-group">
            <button className="btn" style={{ width: '100%' }} onClick={onSendTest} disabled={testJob?.status === 'pending' || testJob?.status === 'sent'}>
              📨 Send test label
            </button>
            {testDisplay && (
              <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: testJob.status === 'done' ? 600 : 400, color: testDisplay.color }}>
                {testDisplay.text}
              </div>
            )}
            <div style={{ fontSize: '11px', color: '#9A9A9A', marginTop: '4px' }}>
              Fires one real ZPL job through the same relay path a live print would use.
            </div>
          </div>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Overlay variant="sheet" onClose={onClose}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#E0E0E0' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 12px', borderBottom: '1px solid #F0F0F0' }}>
          <h4 style={{ margin: 0, fontSize: '15px' }}>{isNew ? 'Add printer' : printer.name}</h4>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '14px 16px' }}>{body}</div>
        <div style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderTop: '1px solid #F0F0F0' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Overlay>
    );
  }

  return ReactDOM.createPortal(
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1400 }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', zIndex: 1401,
        background: 'white', boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #F0F0F0' }}>
          <h4 style={{ margin: 0, fontSize: '16px' }}>{isNew ? 'Add printer' : printer.name}</h4>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>{body}</div>
        <div style={{ display: 'flex', gap: '10px', padding: '14px 20px', borderTop: '1px solid #F0F0F0' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
} 